'use client';

/**
 * ServicesGrid Component
 * 
 * Обновленная версия для вывода Service_description в сетке 2x2.
 */

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { ServiceDescription, getStrapiMedia } from '@/lib/strapi';

const UI_TEXT = {
  ru: { 
    title: "Наши услуги", 
    subtitle: "Профессиональный подход к каждому документу",
    learnMore: "Подробнее" 
  },
  ro: { 
    title: "Serviciile noastre", 
    subtitle: "Abordare profesională pentru fiecare document",
    learnMore: "Mai mult"
  },
  en: { 
    title: "Our Services", 
    subtitle: "Professional approach to every document",
    learnMore: "Learn more"
  },
  uk: { 
    title: "Наші послуги", 
    subtitle: "Професійний підхід до каждого документу",
    learnMore: "Докладніше"
  },
} as const;

// Демо-данные для подстраховки
const getFallbacks = (lang: string): ServiceDescription[] => [
  { 
    id: 1, 
    documentId: 'f1', 
    name: lang === 'ru' ? 'Нотариальный перевод' : 'Notary Translation', 
    slug: 'notary-translation', 
    shot: [], 
    long: [],
    Img: null 
  },
  { 
    id: 2, 
    documentId: 'f2', 
    name: lang === 'ru' ? 'Технический перевод' : 'Technical Translation', 
    slug: 'technical-translation', 
    shot: [], 
    long: [],
    Img: null 
  },
  { 
    id: 3, 
    documentId: 'f3', 
    name: lang === 'ru' ? 'Медицинский перевод' : 'Medical Translation', 
    slug: 'medical-translation', 
    shot: [], 
    long: [],
    Img: null 
  },
  { 
    id: 4, 
    documentId: 'f4', 
    name: lang === 'ru' ? 'Апостиль и легализация' : 'Apostille & Legalization', 
    slug: 'apostille', 
    shot: [], 
    long: [],
    Img: null 
  },
];

interface Props {
  services: ServiceDescription[];
  lang: string;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring" as const,
      stiffness: 100,
      damping: 20,
    },
  },
};

export default function ServicesGrid({ services, lang }: Props) {
  const t = UI_TEXT[lang as keyof typeof UI_TEXT] || UI_TEXT.ru;
  
  // Если данных нет, используем фоллбек
  const hasData = Array.isArray(services) && services.length > 0;
  const displayServices = hasData ? services.slice(0, 4) : getFallbacks(lang);

  return (
    <section className="py-24 bg-white px-6 overflow-hidden min-h-[600px]">
      <div className="max-w-7xl mx-auto">
        {/* Заголовок секции */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20 text-center"
        >
          <h2 className="text-4xl md:text-6xl font-black text-slate-900 mb-6 tracking-tighter">
            {t.title}
          </h2>
          <div className="w-24 h-1.5 bg-blue-600 mx-auto rounded-full mb-8" />
          <p className="text-xl text-slate-500 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Сетка 2x2 на десктопе */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-10"
        >
          {displayServices.map((service, idx) => {
            const mainImg = service.Img && service.Img.length > 0 ? service.Img[0] : null;
            const imgUrl = mainImg ? getStrapiMedia(mainImg.url) : null;

            return (
              <motion.div
                key={service.documentId || `fallback-${idx}`}
                variants={itemVariants}
                whileHover={{ 
                  y: -10,
                  transition: { duration: 0.2 }
                }}
              >
                <Link 
                  href={`/${lang}/services#${service.slug}`}
                  className="group relative flex flex-col bg-slate-50 rounded-[3rem] overflow-hidden transition-all duration-700 hover:shadow-2xl border border-slate-200 min-h-[500px] h-full"
                >
                  {/* Контейнер для картинки */}
                  <div className="relative h-[320px] w-full overflow-hidden bg-slate-100">
                    {imgUrl ? (
                      <Image
                        src={imgUrl}
                        alt={service.name}
                        fill
                        className="object-cover transition-transform duration-700 group-hover:scale-110"
                        sizes="(max-width: 768px) 100vw, 50vw"
                        priority={idx < 2}
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center space-y-4">
                        <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-lg">
                            <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 9a2 0 012-2h.93a2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                              <circle cx="12" cy="13" r="3" />
                            </svg>
                        </div>
                        <span className="text-xs font-bold text-slate-400 tracking-widest uppercase">Service Preview</span>
                      </div>
                    )}
                    {/* Оверлей градиент */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>

                  {/* Контентная часть */}
                  <div className="p-10 flex flex-col justify-between flex-grow">
                    <div>
                      <h3 className="text-3xl font-black text-slate-900 mb-4 group-hover:text-blue-600 transition-colors tracking-tight">
                        {service.name}
                      </h3>
                      
                      <div className="text-xl text-slate-500 line-clamp-3 mb-6 font-light leading-relaxed">
                        {service.shot && service.shot.length > 0 
                          ? service.shot[0].children?.map((c: any) => c.text).join('')
                          : `${service.name} — professional translation in Moldova. Fast processing and accuracy guaranteed.`}
                      </div>
                    </div>

                    <div className="flex items-center text-blue-600 font-black uppercase tracking-widest text-sm">
                      {t.learnMore}
                      <svg className="ml-2 w-5 h-5 transition-transform duration-300 group-hover:translate-x-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </div>
                  </div>

                  {!hasData && (
                    <div className="absolute top-6 left-6 bg-slate-900/10 backdrop-blur-md text-slate-500 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-tighter">
                      DEMO CARD
                    </div>
                  )}
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
